import React, { useEffect, useRef, useState } from "react";
import {
  getSlackConnectionStatus,
  searchSlackTargets,
  sendSlackMessage,
} from "../../businessModeService";
import { startPerformerSlackOAuthFlow } from "../performerGmailOAuth";
import { usageOrErrorMessage } from "./UsageLimitBanner";
import config from "../../../core/config";
import SettingsChipInput from "./SettingsChipInput";
import InlineSendConfirm from "./InlineSendConfirm";

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Search-and-pick a Slack channel or person, then send the given message.
 *
 * First live Slack touch in the send flow — nothing here runs until this
 * component mounts (i.e. until Send → Slack is chosen). No preselect:
 * Slack ingestion is disabled, so there's no source channel/thread to default
 * to — every send starts from a blank search, always posting a genuinely new
 * top-level message (never a thread reply).
 *
 * If the connected credential is missing send scopes (`chat:write` /
 * `im:write`), search can still work on older read-only grants, but posting
 * a DM fails. We check send-capability up front and offer reconnect in-place
 * instead of only showing "Reconnect Slack to enable sending replies."
 *
 * @example
 * <SlackChannelPicker itemKey="w::k::f" message="Hi team, ..." onSent={(written) => ...} />
 */
const SlackChannelPicker = ({ itemKey, message, onCancel, onSent, onError }) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [localError, setLocalError] = useState("");
  const [sendBlocked, setSendBlocked] = useState(null); // null | "reconnect" | "connect"
  const [statusChecked, setStatusChecked] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const debounceRef = useRef(null);
  const mountedRef = useRef(true);
  const oauthStopRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      oauthStopRef.current?.();
    };
  }, []);

  const markSendBlocked = (kind, messageText, { notifyParent = false } = {}) => {
    setSendBlocked(kind);
    setLocalError(messageText);
    // Expected connect/reconnect chrome is rendered in this picker — don't
    // also dump it into the parent, or it reappears after Cancel.
    if (notifyParent) onError?.(messageText);
  };

  const checkSendReady = async () => {
    try {
      const status = await getSlackConnectionStatus();
      if (!mountedRef.current) return false;
      if (!status?.connected) {
        markSendBlocked("connect", "Slack is not connected.");
        return false;
      }
      if (status?.has_send_scope !== true) {
        markSendBlocked("reconnect", "Reconnect Slack to send.");
        return false;
      }
      setSendBlocked(null);
      return true;
    } catch {
      return true;
    }
  };

  const runSearch = async (q) => {
    setLoading(true);
    setLocalError("");
    try {
      const found = await searchSlackTargets(q);
      if (!mountedRef.current) return;
      setChannels(found.channels);
      setPeople(found.people);
    } catch (err) {
      if (!mountedRef.current) return;
      setLocalError(usageOrErrorMessage(err, "Failed to search Slack"));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    void (async () => {
      const ready = await checkSendReady();
      if (!mountedRef.current) return;
      setStatusChecked(true);
      if (!ready) setLoading(false);
    })();
    // Only on mount — searches wait until status is known, then the
    // query-change effect below runs them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!statusChecked || sendBlocked) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void runSearch(query);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sendBlocked, statusChecked]);

  const handleSend = async () => {
    if (!selected || sending || !itemKey || sendBlocked) return;
    setSending(true);
    setLocalError("");
    try {
      const payload =
        selected.type === "channel"
          ? { channel_id: selected.id, message }
          : { user_id: selected.id, message };
      const written = await sendSlackMessage(itemKey, payload);
      onSent?.(written);
    } catch (err) {
      if (err?.missingScope) {
        markSendBlocked(
          "reconnect",
          usageOrErrorMessage(err, "Reconnect Slack to send."),
          { notifyParent: true }
        );
      } else {
        const msg = usageOrErrorMessage(err, "Failed to send Slack message");
        setLocalError(msg);
        onError?.(msg);
      }
    } finally {
      if (mountedRef.current) setSending(false);
    }
  };

  const handleReconnect = async () => {
    if (reconnecting) return;
    oauthStopRef.current?.();
    setReconnecting(true);
    try {
      const authResp = await fetch(
        `${config.BACKEND_URL}/externalauth/slack/auth_url`,
        { method: "GET", credentials: "include" }
      );
      const authPayload = await authResp.json();
      const authUrl = authPayload.auth_url || "";
      if (!authUrl) {
        if (mountedRef.current) setReconnecting(false);
        return;
      }
      oauthStopRef.current = startPerformerSlackOAuthFlow(
        authUrl,
        () => {
          if (!mountedRef.current) return;
          setReconnecting(false);
          setSendBlocked(null);
          setLocalError("");
          setStatusChecked(true);
        },
        () => {
          if (mountedRef.current) setReconnecting(false);
        }
      );
    } catch {
      if (mountedRef.current) setReconnecting(false);
    }
  };

  const hasResults = channels.length > 0 || people.length > 0;

  const inputValue = selected
    ? selected.type === "channel"
      ? `#${selected.name}`
      : selected.name
    : query;

  const slackSendHint = selected
    ? `Post to ${selected.type === "channel" ? `#${selected.name}` : selected.name}?`
    : "";
  const reconnectLabel = sendBlocked === "connect" ? "Connect Slack" : "Reconnect Slack";

  if (!statusChecked) {
    return (
      <div className="ai-draft-expanded__compose" aria-busy="true" aria-label="Send via Slack">
        <p className="ai-draft-expanded__compose-hint">Checking Slack…</p>
      </div>
    );
  }

  return (
    <div className="ai-draft-expanded__compose" aria-label="Send via Slack">
      {sendBlocked ? (
        <p className="ai-draft-expanded__connect-warning">
          {localError || "Slack is not connected."}
        </p>
      ) : (
        <>
          <span className="ai-draft-expanded__compose-label">To</span>
          <div className="ai-draft-expanded__finder">
            <SettingsChipInput
              placeholder="Search Slack"
              value={inputValue}
              onChange={(event) => {
                setSelected(null);
                setQuery(event.target.value);
              }}
              onFocus={() => setDropdownOpen(true)}
              onBlur={() => setDropdownOpen(false)}
              autoComplete="off"
              aria-label="Search Slack channels or people"
              autoFocus
            />
            {dropdownOpen ? (
              <ul className="ai-draft-expanded__finder-suggestions" role="listbox">
                {loading ? (
                  <li className="ai-crm-expanded__name-suggestion ai-crm-expanded__name-suggestion--loading">
                    Searching…
                  </li>
                ) : !hasResults ? (
                  <li className="ai-crm-expanded__name-suggestion ai-crm-expanded__name-suggestion--loading">
                    No matches.
                  </li>
                ) : (
                  <>
                    {channels.length > 0 ? (
                      <>
                        <li className="ai-crm-expanded__candidates-label" aria-hidden="true">
                          Channels
                        </li>
                        {channels.map((channel) => (
                          <li key={channel.id}>
                            <button
                              type="button"
                              className="ai-crm-expanded__name-suggestion"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => {
                                setSelected({
                                  type: "channel",
                                  id: channel.id,
                                  name: channel.name,
                                });
                                setDropdownOpen(false);
                              }}
                            >
                              #{channel.name}
                            </button>
                          </li>
                        ))}
                      </>
                    ) : null}
                    {people.length > 0 ? (
                      <>
                        <li className="ai-crm-expanded__candidates-label" aria-hidden="true">
                          People
                        </li>
                        {people.map((person) => (
                          <li key={person.user_id}>
                            <button
                              type="button"
                              className="ai-crm-expanded__name-suggestion"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => {
                                setSelected({
                                  type: "person",
                                  id: person.user_id,
                                  name: person.name,
                                });
                                setDropdownOpen(false);
                              }}
                            >
                              {person.name}
                              {person.email ? ` (${person.email})` : ""}
                            </button>
                          </li>
                        ))}
                      </>
                    ) : null}
                  </>
                )}
              </ul>
            ) : null}
          </div>
        </>
      )}

      <div className="ai-draft-expanded__compose-actions">
        {sendBlocked ? (
          <button
            type="button"
            className="ai-text-action-btn ai-draft-expanded__recipient-update"
            onClick={() => void handleReconnect()}
            disabled={reconnecting}
          >
            {reconnecting ? "Opening Slack…" : reconnectLabel}
          </button>
        ) : (
          <InlineSendConfirm
            hint={slackSendHint}
            disabled={sending || !selected}
            loading={sending}
            onConfirm={() => void handleSend()}
          />
        )}
        <button
          type="button"
          className="ai-text-action-btn ai-draft-expanded__recipient-update"
          onClick={() => onCancel?.()}
          disabled={sending || reconnecting}
        >
          Cancel
        </button>
      </div>
      {localError && !sendBlocked ? (
        <p className="ai-draft-expanded__finder-error">{localError}</p>
      ) : null}
    </div>
  );
};

export default SlackChannelPicker;
